import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import { CustomInput, CustomSelect, CustomToggle } from '../Components/CustomFormElements';
import PageLoader from '../Components/PageLoader';
import {
  DocumentCollectionApiProvider,
  useDocumentCollectionApi,
} from '../Services/DocumentCollectionApiProvider';
import { isEmpty } from '../utils/validationUtil';

function DocumentCollectionDetail({ canEdit }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { organizationId, collectionId } = useParams();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const {
    createDocumentCollection,
    updateDocumentCollectionById,
    getDocumentCollectionById,
    getDocuments,
  } = useDocumentCollectionApi();

  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [collectionDocuments, setCollectionDocuments] = useState([]);
  const [error, setError] = useState(null);
  const [isNew, setIsNew] = useState(true);
  const [isDisabled, setIsDisabled] = useState(true);

  const [collectionName, setCollectionName] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    const func = async () => {
      setLoading(true);
      try {
        const resp = await getDocuments();
        const list = Array.isArray(resp) ? resp : (resp?.items ?? []);
        setDocuments(list);

        if (collectionId !== 'new-collection') {
          const response = await getDocumentCollectionById(collectionId);
          setCollectionName(response.name);
          setIsActive(response.is_active);
          setCollectionDocuments(response.documents);
        }
      } catch (error) {
        setError('Error fetching document collection data');
        console.error('Error fetching document collection data:', error);
      } finally {
        setLoading(false);
      }
    };
    func();
  }, [collectionId]);

  useEffect(() => {
    setIsNew(collectionId === 'new-collection');
    setIsDisabled(!canEdit);
  }, [collectionId]);

  const onSave = async () => {
    if (isEmpty(collectionName)) {
      showAlert('fail', 'Collection name is required');
      return;
    }

    const collection = {
      name: collectionName,
      is_active: isActive,
      documents: collectionDocuments,
    };

    setLoading(true);

    try {
      if (isNew) {
        await createDocumentCollection(collection);
        showAlert('success', 'Document Collection created successfully');
      } else {
        await updateDocumentCollectionById(collectionId, collection);
        showAlert('success', 'Document Collection updated successfully');
      }
      navigate(`/organizations/${organizationId}/document-collections`);
    } catch (error) {
      console.error('Error saving document collection:', error);
      showAlert('fail', 'Failed to save document collection');
    } finally {
      setLoading(false);
    }
  };

  const onCancel = () => {
    navigate(`/organizations/${organizationId}/document-collections`);
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-6">
          <h2 className="h2-title">{collectionName || 'New Collection'}</h2>
          <div className="gap d-flex flex-row-to-col flex-column">
            <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
              <h4 className="h4-title">Main Settings</h4>

              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Collection name</div>
                  <div className="small-font">Enter collection name</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomInput
                    id="collection-name"
                    label="Collection Name"
                    placeholder="Enter collection name"
                    type="text"
                    value={collectionName}
                    onChange={(id, value) => setCollectionName(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Documents</div>
                  <div className="small-font">Select documents (optional)</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomSelect
                    id="documents-select"
                    label="Documents"
                    options={(documents || []).map((a) => ({
                      value: a.id,
                      label: a.original_name,
                    }))}
                    value={collectionDocuments}
                    multiSelect={true}
                    maxItems={100}
                    onChange={(val) => setCollectionDocuments(val)}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Activate collection</div>
                  <div className="small-font">Set collection to be active or inactive</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomToggle
                    id="toggle-status"
                    label={isActive ? 'Active' : 'Inactive'}
                    checked={isActive}
                    onChange={(id, value) => setIsActive(value)}
                    disabled={false}
                  />
                </div>
              </div>
            </div>

            <div className="buttons-container d-flex justify-end">
              <Button variant="primary" size="medium" disabled={isDisabled} onClick={onSave}>
                Save
              </Button>
              <Button variant="danger" size="medium" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DocumentCollectionDetailWrapper({ canEdit }) {
  return (
    <ConfirmationProvider>
      <DocumentCollectionApiProvider>
        <DocumentCollectionDetail canEdit={canEdit} />
      </DocumentCollectionApiProvider>
    </ConfirmationProvider>
  );
}

export default DocumentCollectionDetailWrapper;
